import { nanoid } from 'nanoid';

import { INQ_DATA_PRESENTATION_SESSION_ID } from 'approot/shared/data-tracker/data-tracker.constants';
import {
  EVENT_ACTION_CLOSE,
  EVENT_ACTION_EDIT,
  EVENT_ACTION_EXIT,
  EVENT_ACTION_EXPAND,
  EVENT_ACTION_FOLLOW,
  EVENT_ACTION_OPEN,
  EVENT_ACTION_VIEW,
  EVENT_CATEGORY_ACTIVITY_BUILDER,
  EVENT_CATEGORY_ACTIVITY_COMPONENT,
  EVENT_CATEGORY_NAV,
  EVENT_CATEGORY_STIMULUS_COMPONENT,
  EVENT_TARGET_IDENTIFIER_TEACHING_NOTES,
  EVENT_TARGET_IDENTIFIER_TEACHING_NOTES_ACTIVITY_NAV_MENU,
  EVENT_TARGET_TYPE_ACTIVITIES,
  EVENT_TARGET_TYPE_DRAWER,
  EVENT_TARGET_TYPE_MENU,
  EVENT_TARGET_TYPE_NAV_ITEM,
  EVENT_TARGET_TYPE_SECTION,
  EVENT_TARGET_TYPE_STIMULUS,
} from 'approot/shared/data-tracker/data-events.constants';
import {
  ACTIVITIES_CONTENT_STATUS_DRAFT,
  ACTIVITIES_CONTENT_STATUS_PUBLISHED,
  ASSESSMENT,
  ASSESSMENT_ACTIVITIES,
  ActivitiesContentStatus,
  LESSON,
  LESSON_ACTIVITIES,
} from 'global/constants';
import { SectionOrSubSectionOverview } from '../activity-builder.constants';
import { ExtraSectionIdentifiers } from '../activity-builder.apollo';
import { getActivityDetailsFromOverview } from '../logic/get-activity-details-from-overview';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { StudentSectionOrSubSectionOverview } from '../student/student.constants';
import { ComponentStimulusType } from '../components/component.constants';

export const ACTIVITY_BUILDER_EVENT_SECTION_VIEW = 'section-view';
export const ACTIVITY_BUILDER_EVENT_TEACHING_NOTES_NAV_ITEM =
  'teaching-notes-nav-item';
export const ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM = 'activities-nav-item';
export const ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND = 'component-expand';
export const ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN = 'stimulus-open';

export type ComponentExpandModeType =
  | typeof EVENT_ACTION_VIEW
  | typeof EVENT_ACTION_EDIT
  | undefined;

export type ActivityBuilderEventType =
  | typeof ACTIVITY_BUILDER_EVENT_SECTION_VIEW
  | typeof ACTIVITY_BUILDER_EVENT_TEACHING_NOTES_NAV_ITEM
  | typeof ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND
  | typeof ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN
  | typeof ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM;

export type ComponentEventData = {
  activityId: string | null;
  index: number;
  isZoomToFit: boolean;
  version: string | undefined;
  status: ActivitiesContentStatus;
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null;
  contentType: typeof LESSON | typeof ASSESSMENT;
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[];
};

export function setPresentationSessionId() {
  localStorage.setItem(INQ_DATA_PRESENTATION_SESSION_ID, nanoid());
}

export function clearPresentationSessionId() {
  localStorage.removeItem(INQ_DATA_PRESENTATION_SESSION_ID);
}

export function generateDataTrackingSequenceInfo(
  sectionId: string,
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null,
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[]
) {
  let activityNumber = 0;
  let currentStep = null;
  let parentSectionId: string | null = sectionId;

  if (sectionId === ExtraSectionIdentifiers.Conclusion) {
    activityNumber = -1;
  } else if (sectionId !== ExtraSectionIdentifiers.Introduction) {
    if (currentSectionOverview) {
      const details = getActivityDetailsFromOverview({
        section: currentSectionOverview,
        sectionsOverview,
      });
      activityNumber = details.activityNumber;
      currentStep = details.currentStep;
      parentSectionId = details.parentSectionId;
    }
  }

  return {
    activityNumber,
    currentStep,
    parentSectionId,
  };
}

type ActivityBuilderPresentViewEventProps = {
  activityId: string | null;
  sectionId: string;
  contentType: typeof LESSON | typeof ASSESSMENT;
  contentId: number;
  version: string | undefined;
  status: ActivitiesContentStatus | undefined;
  index: number;
  isZoomToFit: boolean;
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null;
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[];
  eventType: ActivityBuilderEventType;
};

type ActivityBuilderOptionalEventProps = {
  componentId: string;
  componentType: string;
  expandMode: ComponentExpandModeType;
  stimulusId: number | null;
  vocabularyWord: string;
  vocabularyWordCount: number;
  [key: string]: any;
};

export function pushActivityBuilderPresentViewEvent({
  activityId,
  sectionId,
  contentType,
  contentId,
  version,
  status,
  index,
  isZoomToFit,
  currentSectionOverview,
  sectionsOverview,
  eventType,
  componentId,
  componentType,
  expandMode: expandModeVariant,
  stimulusId,
  vocabularyWord,
  vocabularyWordCount,
}: ActivityBuilderPresentViewEventProps &
  Partial<ActivityBuilderOptionalEventProps>) {
  const { activityNumber, currentStep } = generateDataTrackingSequenceInfo(
    sectionId,
    currentSectionOverview,
    sectionsOverview
  );

  function getActivityLabel(sectionId: string) {
    if (sectionId === ExtraSectionIdentifiers.Introduction) {
      return 'Introduction';
    } else if (sectionId === ExtraSectionIdentifiers.Conclusion) {
      return 'Conclusion';
    } else {
      return `Activity ${activityNumber}`;
    }
  }

  function getEventDetails(eventType: ActivityBuilderEventType | undefined) {
    switch (eventType) {
      case ACTIVITY_BUILDER_EVENT_TEACHING_NOTES_NAV_ITEM:
        return {
          eventCategory: EVENT_CATEGORY_NAV,
          eventAction: EVENT_ACTION_FOLLOW,
          targetIdentifier: getActivityLabel(sectionId),
          targetType: EVENT_TARGET_TYPE_NAV_ITEM,
          navType: EVENT_TARGET_TYPE_MENU,
          navId: EVENT_TARGET_IDENTIFIER_TEACHING_NOTES_ACTIVITY_NAV_MENU,
        };
      case ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND:
        return {
          eventCategory: EVENT_CATEGORY_ACTIVITY_COMPONENT,
          eventAction: EVENT_ACTION_EXPAND,
          targetIdentifier: componentId,
          targetType: componentType || '',
          componentSectionId: sectionId,
          expandMode: expandModeVariant,
        };
      case ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN:
        return {
          eventCategory: EVENT_CATEGORY_STIMULUS_COMPONENT,
          eventAction: EVENT_ACTION_OPEN,
          targetIdentifier: stimulusId || 0,
          targetType: EVENT_TARGET_TYPE_STIMULUS,
          targetSubtype: componentType,
        };
      case ACTIVITY_BUILDER_EVENT_SECTION_VIEW:
      default:
        return {
          eventCategory: EVENT_CATEGORY_ACTIVITY_BUILDER,
          eventAction: EVENT_ACTION_VIEW,
          targetIdentifier: sectionId,
          targetType: EVENT_TARGET_TYPE_SECTION,
        };
    }
  }

  const {
    eventCategory,
    eventAction,
    targetIdentifier,
    targetType,
    navType,
    navId,
    expandMode,
    componentSectionId,
    targetSubtype,
  } = getEventDetails(eventType);

  pushDataTrackerEvent(eventCategory, eventAction, {
    target_type: targetType,
    target_identifier: targetIdentifier,
    ...(navType && {
      nav_type: navType,
    }),
    ...(navId && {
      nav_id: navId,
    }),
    ...(expandMode && {
      expand_mode: expandMode,
    }),
    ...(componentSectionId && {
      section_id: componentSectionId,
    }),
    ...(targetSubtype && {
      target_subtype: targetSubtype,
    }),
    ...(vocabularyWord && {
      vocab_word: vocabularyWord,
    }),
    ...(vocabularyWordCount && {
      vocab_count: vocabularyWordCount,
    }),
    content_type: contentType,
    content_id: contentId,
    activity_id: activityId,
    version: version || null,
    status: status || ACTIVITIES_CONTENT_STATUS_DRAFT,
    activity_sequence_in_activities: activityNumber, // activity number shown in differentation shape
    section_sequence_in_activities: index,
    section_sequence_in_activity: currentStep, // 1 unless this is a sub-slide (i.e. part 2 of 2)
    fit_to_screen: isZoomToFit,
  });
}

export function getTeachingNotesDrawerTrackingData({
  sectionId,
  currentSectionOverview,
  sectionsOverview,
  contentType,
  contentId,
  index,
  isZoomToFit,
  status,
  version,
}: {
  sectionId: string;
  currentSectionOverview: SectionOrSubSectionOverview | null;
  sectionsOverview: SectionOrSubSectionOverview[];
  contentType: typeof LESSON | typeof ASSESSMENT;
  contentId: number;
  index: number;
  isZoomToFit: boolean;
  status: ActivitiesContentStatus | undefined;
  version: string | undefined;
}) {
  const { activityNumber, parentSectionId, currentStep } =
    generateDataTrackingSequenceInfo(
      sectionId,
      currentSectionOverview,
      sectionsOverview
    );
  return {
    category: EVENT_CATEGORY_NAV,
    target: {
      target_identifier: EVENT_TARGET_IDENTIFIER_TEACHING_NOTES,
      target_type: EVENT_TARGET_TYPE_DRAWER,
      content_type: contentType,
      content_id: contentId,
      activity_id: parentSectionId || sectionId,
      section_id: sectionId,
      activity_sequence_in_activities: activityNumber,
      section_sequence_in_activities: index + 1,
      section_sequence_in_activity: currentStep,
      fit_to_screen: isZoomToFit,
      status: status || ACTIVITIES_CONTENT_STATUS_DRAFT,
      version: version || null,
    },
  };
}

export function getUpdatedSectionDetails(
  sectionId: string,
  sortedSectionsAndSubSectionsOverview: SectionOrSubSectionOverview[]
) {
  const newSectionOverview =
    sortedSectionsAndSubSectionsOverview.find(s => s.id === sectionId) || null;
  const index =
    sectionId === ExtraSectionIdentifiers.Conclusion
      ? sortedSectionsAndSubSectionsOverview.length + 1
      : sortedSectionsAndSubSectionsOverview.findIndex(
          s => s.id === sectionId
        ) + 1;

  return { newSectionOverview, index };
}

export function getUpdatedStudentSectionDetails(
  sectionId: string,
  sortedSectionsAndSubSectionsOverview: StudentSectionOrSubSectionOverview[]
) {
  const newSectionOverview =
    sortedSectionsAndSubSectionsOverview.find(s => s.id === sectionId) || null;
  const index =
    sectionId === ExtraSectionIdentifiers.Conclusion
      ? sortedSectionsAndSubSectionsOverview.length + 1
      : sortedSectionsAndSubSectionsOverview.findIndex(
          s => s.id === sectionId
        ) + 1;

  return { newSectionOverview, index };
}

export function pushComponentViewEvent({
  component,
  eventType,
  expandMode,
  eventTrackingData,
  contentId,
  vocabularyWord,
  vocabularyWordCount,
}: {
  component: ComponentStimulusType;
  eventType: ActivityBuilderEventType;
  expandMode?: ComponentExpandModeType;
  eventTrackingData: ComponentEventData | undefined;
  contentId: number;
  vocabularyWord?: string;
  vocabularyWordCount?: number;
}) {
  if (eventTrackingData) {
    const {
      activityId,
      index,
      isZoomToFit,
      version,
      currentSectionOverview,
      status,
      sectionsOverview,
      contentType,
    } = eventTrackingData;

    const baseEventData = {
      activityId,
      contentId,
      eventType,
      sectionId: currentSectionOverview?.id || '',
      componentType: component.type,
      contentType,
      status,
      version,
      index,
      isZoomToFit,
      currentSectionOverview,
      sectionsOverview,
    };
    const extraEventData =
      eventType === ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND
        ? {
            componentId: component.id,
            expandMode:
              component.type === 'vocabulary'
                ? undefined
                : expandMode || EVENT_ACTION_VIEW,
            vocabularyWord,
            vocabularyWordCount,
          }
        : { stimulusId: component?.properties?.stimulusId };

    pushActivityBuilderPresentViewEvent({
      ...baseEventData,
      ...extraEventData,
    });
  }
}

export function pushActivityBuilderPresentMenuEvent({
  eventAction,
  eventTargetIdentifier,
  contentId,
  contentType,
  sectionId,
  currentSectionOverview,
  currentSectionOverviewIndex,
  sectionsOverview,
  version,
  status,
  isZoomToFit,
}: {
  eventAction:
    | typeof EVENT_ACTION_OPEN
    | typeof EVENT_ACTION_CLOSE
    | typeof EVENT_ACTION_EXIT;
  eventTargetIdentifier:
    | typeof EVENT_TARGET_TYPE_MENU
    | typeof EVENT_TARGET_TYPE_ACTIVITIES;
  contentId: number;
  contentType: typeof LESSON_ACTIVITIES | typeof ASSESSMENT_ACTIVITIES;
  sectionId: string;
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null;
  currentSectionOverviewIndex: number;
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[];
  version?: string;
  status?:
    | typeof ACTIVITIES_CONTENT_STATUS_PUBLISHED
    | typeof ACTIVITIES_CONTENT_STATUS_DRAFT;
  isZoomToFit: boolean;
}) {
  const { activityNumber, currentStep } = generateDataTrackingSequenceInfo(
    sectionId,
    currentSectionOverview,
    sectionsOverview
  );

  const pageIndex =
    sectionId === ExtraSectionIdentifiers.Introduction
      ? 0
      : sectionId === ExtraSectionIdentifiers.Conclusion
        ? sectionsOverview.length + 1
        : currentSectionOverviewIndex + 1;

  pushDataTrackerEvent(EVENT_CATEGORY_NAV, eventAction, {
    target_type: EVENT_TARGET_TYPE_DRAWER,
    target_identifier: eventTargetIdentifier,
    content_id: contentId,
    content_type: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
    activity_id: sectionId,
    version,
    status,
    activity_sequence_in_activities: activityNumber, // activity number shown in differentation shape
    section_sequence_in_activities: pageIndex,
    section_sequence_in_activity: currentStep, // 1 unless this is a sub-slide (i.e. part 2 of 2)
    fit_to_screen: isZoomToFit,
  });
}

export function pushActivityBuilderPresentActivitiesEvent({
  contentId,
  contentType,
  sectionId,
  clickedSectionIndex,
  currentSectionOverview,
  currentSectionOverviewIndex,
  sectionsOverview,
  version,
  status,
  isZoomToFit,
}: {
  contentId: number;
  contentType: typeof LESSON_ACTIVITIES | typeof ASSESSMENT_ACTIVITIES;
  sectionId: string;
  clickedSectionIndex: number;
  currentSectionOverview:
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
    | null;
  currentSectionOverviewIndex: number;
  sectionsOverview:
    | SectionOrSubSectionOverview[]
    | StudentSectionOrSubSectionOverview[];
  version?: string;
  status?:
    | typeof ACTIVITIES_CONTENT_STATUS_PUBLISHED
    | typeof ACTIVITIES_CONTENT_STATUS_DRAFT;
  isZoomToFit: boolean;
}) {
  const { activityNumber, currentStep } = generateDataTrackingSequenceInfo(
    sectionId,
    currentSectionOverview,
    sectionsOverview
  );

  const pageIndex =
    sectionId === ExtraSectionIdentifiers.Introduction
      ? 0
      : sectionId === ExtraSectionIdentifiers.Conclusion
        ? sectionsOverview.length + 1
        : currentSectionOverviewIndex + 1;

  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: clickedSectionIndex + 1, // +1 is because the introduction is -1
    nav_id: 'activities preview',
    nav_type: EVENT_TARGET_TYPE_MENU,
    content_id: contentId,
    content_type: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
    activity_id: sectionId,
    version,
    status,
    activity_sequence_in_activities: activityNumber, // activity number shown in differentation shape
    section_sequence_in_activities: pageIndex,
    section_sequence_in_activity: currentStep, // 1 unless this is a sub-slide (i.e. part 2 of 2)
    fit_to_screen: isZoomToFit,
  });
}
